import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  PROJECT_HEALTH_LABELS,
  PROJECT_HEALTH_ORDER,
  PROJECT_PRIORITY_COLORS,
  PROJECT_PRIORITY_LABELS,
  displayName,
} from '../../pm/constants';
import { formatMonthDay } from '../../pm/dueDate';
import { computeHealth } from '../../pm/health';
import {
  compareProjectsForRoadmap,
  ProjectRange,
  resolveProjectRange,
  ROADMAP_LANES,
} from '../../pm/roadmap';
import { useProjectDepartments } from '../../pm/useProjectDepartments';
import {
  AppUser,
  Project,
  ProjectHealth,
  ProjectPriority,
  Task,
} from '../../pm/types';

interface Props {
  projects: Project[];
  tasks: Task[];
  /** Admin pool — used to derive the Owner filter's options (label =
   *  display name from email) and to resolve uids back to names in the
   *  dropdown. */
  admins: AppUser[];
  /** When provided, clicking a bar opens the drawer instead of navigating
   *  straight to the project detail page. */
  onOpenDrawer?: (projectId: string) => void;
}

type RoadmapGroup = 'priority' | 'department';
type RoadmapZoom = 'week' | 'month' | 'quarter';
type FilterId = 'group' | 'owner' | 'status' | null;

const DAY = 86400000;
const PAD_DAYS = 7;

// Adaptive cadence the auto-fit roadmap used. Now only used as the default
// (zoom === 'month'). The other zoom levels override with fixed step sizes
// so the user gets visible feedback from the zoom segmented control.
function chooseTickCadence(spanDays: number): { stepDays: number; format: 'day' | 'month' } {
  if (spanDays <= 28)  return { stepDays: 7,   format: 'day'   };
  if (spanDays <= 120) return { stepDays: 14,  format: 'day'   };
  if (spanDays <= 365) return { stepDays: 30,  format: 'month' };
  return                       { stepDays: 90, format: 'month' };
}

function tickCadenceForZoom(spanDays: number, zoom: RoadmapZoom):
  { stepDays: number; format: 'day' | 'month' } {
  if (zoom === 'week')    return { stepDays: 1,  format: 'day'   };
  if (zoom === 'quarter') return { stepDays: 30, format: 'month' };
  // 'month' falls back to the adaptive choice so the axis still reads
  // sensibly across very short and very long roadmaps.
  return chooseTickCadence(spanDays);
}

function startOfDay(ms: number): number {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

// "Is this project complete?" — all of its tasks are done, and it has at
// least one task. A project with zero tasks is not considered completed
// (you can't ship empty).
function isProjectComplete(projectId: string, tasks: Task[]): boolean {
  let saw = false;
  for (const t of tasks) {
    if (t.projectId !== projectId) continue;
    saw = true;
    if (t.status !== 'done') return false;
  }
  return saw;
}

interface ScheduledRow {
  project: Project;
  range: ProjectRange;
  completed: boolean;
  total: number;
  done: number;
}

const RoadmapView: React.FC<Props> = ({ projects, tasks, admins, onOpenDrawer }) => {
  const history = useHistory();
  const [showCompleted, setShowCompleted] = useState(false);
  // Toolbar state — every chip / segmented control is wired through here.
  const [zoom, setZoom] = useState<RoadmapZoom>('month');
  const [groupBy, setGroupBy] = useState<RoadmapGroup>('priority');
  const [ownerFilter, setOwnerFilter] = useState<string>('all'); // uid or 'all'
  const [statusFilter, setStatusFilter] = useState<ProjectHealth | 'all'>('all');
  const [openFilter, setOpenFilter] = useState<FilterId>(null);

  // Click-outside dismisses any open filter popout.
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!openFilter) return;
    const onDown = (e: MouseEvent) => {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target as Node)) {
        setOpenFilter(null);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [openFilter]);

  const { departments, getDept } = useProjectDepartments();
  const nowMs = Date.now();

  // Per-project rows + the unscheduled tray. We compute completion stats here
  // so the swimlane bars can render a progress fill and we don't loop tasks
  // twice.
  const { scheduled, unscheduled, completedCount } = useMemo(() => {
    const taskCounts: Record<string, { total: number; done: number }> = {};
    for (const t of tasks) {
      if (!taskCounts[t.projectId]) taskCounts[t.projectId] = { total: 0, done: 0 };
      taskCounts[t.projectId].total++;
      if (t.status === 'done') taskCounts[t.projectId].done++;
    }
    const scheduledRows: ScheduledRow[] = [];
    const unscheduledRows: Project[] = [];
    let completed = 0;
    for (const p of projects) {
      const range = resolveProjectRange(p, tasks, nowMs);
      const counts = taskCounts[p.id] || { total: 0, done: 0 };
      const isDone = isProjectComplete(p.id, tasks);
      if (isDone) completed++;
      if (!range) {
        unscheduledRows.push(p);
        continue;
      }
      scheduledRows.push({ project: p, range, completed: isDone, total: counts.total, done: counts.done });
    }
    return { scheduled: scheduledRows, unscheduled: unscheduledRows, completedCount: completed };
  }, [projects, tasks, nowMs]);

  // Set of project IDs that match the owner filter — derived from tasks so
  // a project is "owned by X" if any of its tasks is owned by X. We compute
  // this once and use it as a fast Set.has during row filtering.
  const ownerMatchProjectIds = useMemo(() => {
    if (ownerFilter === 'all') return null;
    const s = new Set<string>();
    for (const t of tasks) {
      if (t.ownerId === ownerFilter) s.add(t.projectId);
    }
    return s;
  }, [tasks, ownerFilter]);

  // Apply all filters to scheduled rows: hide-completed, owner, status.
  // We pass `tasks` to computeHealth per project rather than slicing it up
  // front because the cost is low (each call is O(n_tasks)) and keeping
  // the call inline makes the filter logic legible.
  const visibleScheduled = useMemo(() => {
    return scheduled.filter((r) => {
      if (!showCompleted && r.completed) return false;
      if (ownerMatchProjectIds && !ownerMatchProjectIds.has(r.project.id)) return false;
      if (statusFilter !== 'all') {
        const health = computeHealth(r.project, tasks, nowMs);
        if (health.state !== statusFilter) return false;
      }
      return true;
    });
  }, [scheduled, showCompleted, ownerMatchProjectIds, statusFilter, tasks, nowMs]);

  // Global axis range across every bar we're drawing. Without any visible
  // ranges, we fall back on "today ± 30 days" so the empty axis still reads
  // as a calendar.
  const globalRange = useMemo(() => {
    if (visibleScheduled.length === 0) {
      return { min: nowMs - 30 * DAY, max: nowMs + 30 * DAY };
    }
    let min = Infinity;
    let max = -Infinity;
    for (const row of visibleScheduled) {
      if (row.range.start < min) min = row.range.start;
      if (row.range.end > max) max = row.range.end;
    }
    const pad = PAD_DAYS * DAY;
    if (min === max) return { min: min - pad, max: max + pad };
    return { min: min - pad, max: max + pad };
  }, [visibleScheduled, nowMs]);

  const axisTicks = useMemo(() => {
    const spanDays = Math.max(1, Math.round((globalRange.max - globalRange.min) / DAY));
    const { stepDays, format } = tickCadenceForZoom(spanDays, zoom);
    const stepMs = stepDays * DAY;
    const ticks: { ms: number; label: string }[] = [];
    let cursor = startOfDay(globalRange.min);
    while (cursor < globalRange.min) cursor += stepMs;
    while (cursor <= globalRange.max) {
      const d = new Date(cursor);
      const label = format === 'day'
        ? formatMonthDay(cursor)
        : d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
      ticks.push({ ms: cursor, label });
      cursor += stepMs;
    }
    return ticks;
  }, [globalRange, zoom]);

  // Lane key for a project. Priority mode uses the priority enum (with
  // 'unprioritized' fallback); Department mode uses the department id
  // (with 'unassigned' fallback). The lane LIST below is computed
  // separately so we render lanes in a stable order, not just whatever
  // shows up in the data.
  function keyForLane(p: Project): string {
    if (groupBy === 'department') return p.department || 'unassigned';
    return p.priority || 'unprioritized';
  }

  // Rows bucketed by lane, in display order.
  const rowsByLane = useMemo(() => {
    const m: Record<string, ScheduledRow[]> = {};
    for (const row of visibleScheduled) {
      const key = keyForLane(row.project);
      if (!m[key]) m[key] = [];
      m[key].push(row);
    }
    for (const k of Object.keys(m)) {
      m[k].sort((a, b) => compareProjectsForRoadmap(a.project, b.project));
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleScheduled, groupBy]);

  const showTodayLine = nowMs >= globalRange.min && nowMs <= globalRange.max;

  function pct(ms: number): number {
    const span = globalRange.max - globalRange.min;
    if (span <= 0) return 0;
    return ((ms - globalRange.min) / span) * 100;
  }

  function openProject(p: Project) {
    if (onOpenDrawer) {
      onOpenDrawer(p.id);
    } else {
      history.push(`/Projects/${p.id}`);
    }
  }

  // Lane color + label resolve from groupBy:
  //   Priority mode → PROJECT_PRIORITY_COLORS / LABELS (with 'unprioritized' fallback)
  //   Department mode → swatch + label from useProjectDepartments (with 'unassigned' fallback)
  function laneColor(lane: string): string {
    if (groupBy === 'department') {
      if (lane === 'unassigned') return '#9CA3AF';
      return getDept(lane)?.swatch || '#9CA3AF';
    }
    if (lane === 'unprioritized') return '#9CA3AF';
    return PROJECT_PRIORITY_COLORS[lane as ProjectPriority];
  }

  function laneLabel(lane: string): string {
    if (groupBy === 'department') {
      if (lane === 'unassigned') return 'Unassigned';
      return getDept(lane)?.label || 'Unknown department';
    }
    if (lane === 'unprioritized') return 'Unprioritized';
    return PROJECT_PRIORITY_LABELS[lane as ProjectPriority];
  }

  // Canonical lane order per mode. Priority uses the fixed ROADMAP_LANES;
  // Department uses the RTDB list (already sorted by sortOrder) + a
  // trailing 'unassigned' bucket.
  const allLanesForMode = useMemo<string[]>(() => {
    if (groupBy === 'department') {
      return [...departments.map((d) => d.id), 'unassigned'];
    }
    return ROADMAP_LANES.slice();
  }, [groupBy, departments]);

  // We only render lanes that have at least one project, with one exception:
  // when the roadmap is totally empty we still want the user to see the
  // lane structure, so we keep all lanes visible in that case.
  const lanesToRender = useMemo(() => {
    if (visibleScheduled.length === 0) return allLanesForMode;
    return allLanesForMode.filter((lane) => (rowsByLane[lane] || []).length > 0);
  }, [rowsByLane, visibleScheduled.length, allLanesForMode]);

  function tooltipFor(row: ScheduledRow): string {
    const parts: string[] = [];
    const start = formatMonthDay(row.range.start, { withYear: true });
    if (row.range.kind === 'open-ended') {
      parts.push(`${start} – open-ended`);
    } else {
      const end = formatMonthDay(row.range.end, { withYear: true });
      parts.push(`${start} – ${end}`);
    }
    if (row.total > 0) {
      const pctDone = Math.round((row.done / row.total) * 100);
      parts.push(`${row.done}/${row.total} tasks · ${pctDone}%`);
    } else {
      parts.push('No tasks');
    }
    switch (row.range.kind) {
      case 'explicit':   parts.push('Dates: explicit'); break;
      case 'partial':    parts.push('Dates: partial (one explicit, one from tasks)'); break;
      case 'derived':    parts.push('Dates: derived from tasks'); break;
      case 'open-ended': parts.push('Dates: open-ended'); break;
    }
    return parts.join('\n');
  }

  return (
    <div className="pm-roadmap">
      {/* Roadmap toolbar — design .rm__bar. Each filter chip now opens a
       * small absolute-positioned dropdown beneath it; the chrome is
       * functional from end to end. */}
      <div className="pm-roadmap-topbar" ref={toolbarRef}>
        <div className="pm-roadmap-filters">
          {/* GROUP — Priority vs Department lanes. */}
          <div className="pm-roadmap-filter-wrap">
            <button
              type="button"
              className={`pm-roadmap-filter${openFilter === 'group' ? ' is-open' : ''}`}
              aria-haspopup="listbox"
              aria-expanded={openFilter === 'group'}
              onClick={() => setOpenFilter(openFilter === 'group' ? null : 'group')}
            >
              <span className="pm-roadmap-filter-key">Group:</span>
              <b>{groupBy === 'department' ? 'Department' : 'Priority'}</b>
              <span className="pm-roadmap-filter-chev" aria-hidden="true" />
            </button>
            {openFilter === 'group' ? (
              <div className="pm-roadmap-filter-menu" role="listbox">
                {([
                  { id: 'priority',   label: 'Priority' },
                  { id: 'department', label: 'Department' },
                ] as { id: RoadmapGroup; label: string }[]).map((opt) => (
                  <button
                    key={opt.id}
                    role="option"
                    aria-selected={groupBy === opt.id}
                    className={`pm-roadmap-filter-item${groupBy === opt.id ? ' is-on' : ''}`}
                    onClick={() => { setGroupBy(opt.id); setOpenFilter(null); }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {/* OWNER — derived from task ownership. */}
          <div className="pm-roadmap-filter-wrap">
            <button
              type="button"
              className={`pm-roadmap-filter${openFilter === 'owner' ? ' is-open' : ''}${ownerFilter !== 'all' ? ' is-active' : ''}`}
              aria-haspopup="listbox"
              aria-expanded={openFilter === 'owner'}
              onClick={() => setOpenFilter(openFilter === 'owner' ? null : 'owner')}
            >
              Owner: <b>{ownerFilter === 'all' ? 'All' : displayName(admins.find((a) => a.uid === ownerFilter)?.email) || 'Unknown'}</b>
              <span className="pm-roadmap-filter-chev" aria-hidden="true" />
            </button>
            {openFilter === 'owner' ? (
              <div className="pm-roadmap-filter-menu" role="listbox">
                <button
                  role="option"
                  aria-selected={ownerFilter === 'all'}
                  className={`pm-roadmap-filter-item${ownerFilter === 'all' ? ' is-on' : ''}`}
                  onClick={() => { setOwnerFilter('all'); setOpenFilter(null); }}
                >
                  All owners
                </button>
                {admins.map((a) => (
                  <button
                    key={a.uid}
                    role="option"
                    aria-selected={ownerFilter === a.uid}
                    className={`pm-roadmap-filter-item${ownerFilter === a.uid ? ' is-on' : ''}`}
                    onClick={() => { setOwnerFilter(a.uid); setOpenFilter(null); }}
                  >
                    {displayName(a.email)}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {/* STATUS — health-based (on-track / at-risk / off-track). */}
          <div className="pm-roadmap-filter-wrap">
            <button
              type="button"
              className={`pm-roadmap-filter${openFilter === 'status' ? ' is-open' : ''}${statusFilter !== 'all' ? ' is-active' : ''}`}
              aria-haspopup="listbox"
              aria-expanded={openFilter === 'status'}
              onClick={() => setOpenFilter(openFilter === 'status' ? null : 'status')}
            >
              Status: <b>{statusFilter === 'all' ? 'All' : PROJECT_HEALTH_LABELS[statusFilter]}</b>
              <span className="pm-roadmap-filter-chev" aria-hidden="true" />
            </button>
            {openFilter === 'status' ? (
              <div className="pm-roadmap-filter-menu" role="listbox">
                <button
                  role="option"
                  aria-selected={statusFilter === 'all'}
                  className={`pm-roadmap-filter-item${statusFilter === 'all' ? ' is-on' : ''}`}
                  onClick={() => { setStatusFilter('all'); setOpenFilter(null); }}
                >
                  All statuses
                </button>
                {PROJECT_HEALTH_ORDER.map((s) => (
                  <button
                    key={s}
                    role="option"
                    aria-selected={statusFilter === s}
                    className={`pm-roadmap-filter-item${statusFilter === s ? ' is-on' : ''}`}
                    onClick={() => { setStatusFilter(s); setOpenFilter(null); }}
                  >
                    {PROJECT_HEALTH_LABELS[s]}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <label className="pm-roadmap-toggle">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
            />
            Show completed{completedCount > 0 ? ` (${completedCount})` : ''}
          </label>
        </div>

        {/* ZOOM — overrides axis tick cadence. */}
        <div className="pm-roadmap-zoom" role="tablist" aria-label="Zoom level">
          {(['week', 'month', 'quarter'] as const).map((z) => (
            <button
              key={z}
              type="button"
              role="tab"
              aria-selected={z === zoom}
              className={z === zoom ? 'is-on' : ''}
              onClick={() => setZoom(z)}
            >
              {z}
            </button>
          ))}
        </div>
      </div>

      <div className="pm-roadmap-grid">
        <div className="pm-roadmap-axis">
          <div className="pm-roadmap-axis-track">
            {axisTicks.map((tick) => (
              <div
                key={tick.ms}
                className="pm-roadmap-axis-tick"
                style={{ left: `${pct(tick.ms)}%` }}
              >
                <span className="pm-roadmap-axis-tick-label">{tick.label}</span>
              </div>
            ))}
            {showTodayLine ? (
              <div className="pm-roadmap-today-pill" style={{ left: `${pct(nowMs)}%` }}>
                Today · {formatMonthDay(nowMs)}
              </div>
            ) : null}
          </div>
        </div>

        {lanesToRender.map((lane) => {
          const rows = rowsByLane[lane] || [];
          return (
            <div key={lane} className="pm-roadmap-lane">
              <div className="pm-roadmap-lane-header">
                <span
                  className="pm-roadmap-lane-dot"
                  style={{ background: laneColor(lane) }}
                />
                <span className="pm-roadmap-lane-title">{laneLabel(lane)}</span>
                <span className="pm-roadmap-lane-count">{rows.length}</span>
              </div>

              {rows.length === 0 ? (
                <div className="pm-roadmap-lane-empty">
                  No projects in this lane.
                </div>
              ) : (
                rows.map((row) => {
                  const color = laneColor(lane);
                  const left = pct(row.range.start);
                  const width = Math.max(0.5, pct(row.range.end) - left);
                  const fillPct = row.total > 0 ? Math.round((row.done / row.total) * 100) : 0;
                  const overdue =
                    !row.completed &&
                    row.range.kind !== 'open-ended' &&
                    row.range.end < nowMs;
                  const classes = [
                    'pm-roadmap-row',
                    row.completed ? 'pm-roadmap-row-completed' : '',
                  ].filter(Boolean).join(' ');
                  const barClasses = [
                    'pm-roadmap-bar',
                    `pm-roadmap-bar-${lane}`,
                    row.range.kind === 'open-ended' ? 'pm-roadmap-bar-openended' : '',
                    overdue ? 'pm-roadmap-bar-overdue' : '',
                  ].filter(Boolean).join(' ');
                  return (
                    <div key={row.project.id} className={classes}>
                      <button
                        type="button"
                        className="pm-roadmap-row-label"
                        onClick={() => openProject(row.project)}
                        title={row.project.title}
                      >
                        {row.project.title}
                      </button>
                      <div className="pm-roadmap-row-track">
                        {showTodayLine ? (
                          <div
                            className="pm-roadmap-today-line"
                            style={{ left: `${pct(nowMs)}%` }}
                          />
                        ) : null}
                        <button
                          type="button"
                          className={barClasses}
                          style={{
                            left: `${left}%`,
                            width: `${width}%`,
                            background: color,
                          }}
                          onClick={() => openProject(row.project)}
                          title={tooltipFor(row)}
                        >
                          <span
                            className="pm-roadmap-bar-fill"
                            style={{ width: `${fillPct}%` }}
                          />
                          <span className="pm-roadmap-bar-label">
                            {row.total > 0
                              ? `${row.done}/${row.total}`
                              : 'no tasks'}
                            {overdue ? ' · overdue' : ''}
                          </span>
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          );
        })}
      </div>

      {unscheduled.length > 0 ? (
        <div className="pm-roadmap-unscheduled">
          <div className="pm-roadmap-unscheduled-header">
            Unscheduled ({unscheduled.length})
          </div>
          <p className="pm-roadmap-unscheduled-hint">
            Open a project and set a Start / Target date to place it on the roadmap.
          </p>
          <ul className="pm-roadmap-unscheduled-list">
            {unscheduled.map((p) => (
              <li key={p.id}>
                <span
                  className="pm-roadmap-unscheduled-dot"
                  style={{ background: laneColor(keyForLane(p)) }}
                />
                <button
                  type="button"
                  className="pm-link-button"
                  onClick={() => openProject(p)}
                >
                  {p.title}
                </button>
                {p.priority ? (
                  <span className="pm-roadmap-unscheduled-priority">
                    {PROJECT_PRIORITY_LABELS[p.priority]}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export default RoadmapView;
